// The rules of edit mode on the dashboard's grid (requirements L-6 to L-9 in
// docs/specs/dashboard-editor.md): moving and resizing a tile by whole cells, the smallest size, and
// on a scrolling grid the tiles below a changed tile moving down or back up. Plain functions on
// cell rectangles, so that a gesture is always worked out from the layout it started from and can
// be taken back while it lasts.

/** A tile's place on the grid, as the dashboard has it: 1-based column and row, spans in cells. */
export interface CellRect {
    readonly x: number;
    readonly y: number;
    readonly width: number;
    readonly height: number;
}

/** The dashboard's grid: with `rows` it fills the screen; without, it scrolls and has no bottom. */
export interface EditGrid {
    readonly columns: number;
    readonly rows?: number | undefined;
}

export interface Span {
    readonly width: number;
    readonly height: number;
}

export type Corner = 'top-left' | 'top-right' | 'bottom-left' | 'bottom-right';

/** Grids at least this many cells across have a smallest tile of 2 cells that way (legacy 48 × 48). */
const FINE_GRID_CELLS = 24;

/**
 * The smallest tile: 2 × 2 on a fine grid such as the legacy 48 × 48, 1 × 1 on a grid of few
 * columns such as a phone's 4 (the owner, Q-3). A scrolling grid's rows are as high as its columns
 * are wide, so its columns decide both.
 */
export function minimumSpan(grid: EditGrid) : Span {
    const across = (cells: number) : number => (cells >= FINE_GRID_CELLS ? 2 : 1);

    return { width: across(grid.columns), height: across(grid.rows ?? grid.columns) };
}

export function overlaps(a: CellRect, b: CellRect) : boolean {
    return a.x < b.x + b.width && b.x < a.x + a.width && a.y < b.y + b.height && b.y < a.y + a.height;
}

function sharesColumns(a: CellRect, b: CellRect) : boolean {
    return a.x < b.x + b.width && b.x < a.x + a.width;
}

const bottomOf = (rect: CellRect) : number => rect.y + rect.height;

/** `rect` moved by whole cells, kept inside the grid (a scrolling grid has no bottom edge). */
export function moveRect(rect: CellRect, columns: number, rows: number, grid: EditGrid) : CellRect {
    const width = Math.min(rect.width, grid.columns);
    const height = grid.rows === undefined ? rect.height : Math.min(rect.height, grid.rows);
    const lastY = grid.rows === undefined ? Number.POSITIVE_INFINITY : grid.rows - height + 1;

    return {
        x: clamp(rect.x + columns, 1, grid.columns - width + 1),
        y: clamp(rect.y + rows, 1, lastY),
        width,
        height,
    };
}

/**
 * `rect` resized by dragging `corner` by whole cells: the opposite corner stays where it is, the
 * tile stays inside the grid and is never smaller than `minimum`.
 */
export function resizeRect(rect: CellRect, corner: Corner, columns: number, rows: number, grid: EditGrid, minimum: Span) : CellRect {
    let left = rect.x;
    let right = rect.x + rect.width - 1;
    let top = rect.y;
    let bottom = rect.y + rect.height - 1;
    const lastRow = grid.rows ?? Number.POSITIVE_INFINITY;

    if (corner === 'top-left' || corner === 'bottom-left') {
        left = clamp(left + columns, 1, right - minimum.width + 1);
    } else {
        right = clamp(right + columns, left + minimum.width - 1, grid.columns);
    }
    if (corner === 'top-left' || corner === 'top-right') {
        top = clamp(top + rows, 1, bottom - minimum.height + 1);
    } else {
        bottom = clamp(bottom + rows, top + minimum.height - 1, lastRow);
    }

    return { x: left, y: top, width: right - left + 1, height: bottom - top + 1 };
}

/**
 * The tiles below `rects[index]`: those that start at or below its bottom in its columns, and the
 * tiles below those, and so on. They move together when the tile grows or shrinks.
 */
function tilesBelow(rects: readonly CellRect[], index: number) : Set<number> {
    const below = new Set<number>([index]);
    const order = [...rects.keys()].sort((a, b) => rects[a]!.y - rects[b]!.y);
    let grew = true;

    while (grew) {
        grew = false;
        for (const candidate of order) {
            if (below.has(candidate)) {
                continue;
            }

            const rect = rects[candidate]!;
            const isBelow = [...below].some((member) => sharesColumns(rects[member]!, rect) && rect.y >= bottomOf(rects[member]!));
            if (isBelow) {
                below.add(candidate);
                grew = true;
            }
        }
    }
    below.delete(index);

    return below;
}

/**
 * How far up (a negative number of rows, at most `rows`) the tiles in `moving` can go without
 * running into the others or above the first row.
 */
function upwardRoom(rects: readonly CellRect[], moving: ReadonlySet<number>, rows: number) : number {
    let room = rows;

    for (const member of moving) {
        const rect = rects[member]!;
        room = Math.max(room, 1 - rect.y);
        for (const [other, otherRect] of rects.entries()) {
            if (moving.has(other) || !sharesColumns(rect, otherRect) || bottomOf(otherRect) > rect.y) {
                continue;
            }
            room = Math.max(room, bottomOf(otherRect) - rect.y);
        }
    }

    return room;
}

function shifted(rects: readonly CellRect[], moving: ReadonlySet<number>, rows: number) : CellRect[] {
    return rects.map((rect, index) => (moving.has(index) ? { ...rect, y: rect.y + rows } : rect));
}

/**
 * Pushes every tile that overlaps `rects[index]` down, out of its way, and the tiles they then
 * overlap in turn. The tile at `index` stays where it is; tiles keep their order from top to bottom.
 */
export function makeRoom(rects: readonly CellRect[], index: number) : CellRect[] {
    const result = [...rects];
    const order = [...rects.keys()].filter((other) => other !== index).sort((a, b) => rects[a]!.y - rects[b]!.y || rects[a]!.x - rects[b]!.x);
    const placed = [index];

    for (const other of order) {
        let rect = result[other]!;
        let blocker = placed.find((member) => overlaps(result[member]!, rect));
        while (blocker !== undefined) {
            rect = { ...rect, y: bottomOf(result[blocker]!) };
            blocker = placed.find((member) => overlaps(result[member]!, rect));
        }
        result[other] = rect;
        placed.push(other);
    }

    return result;
}

/**
 * A tile moved on a scrolling grid: the tiles below its old place move up into it, as far as the
 * tiles beside them allow, the tiles at its new place are pushed down, and rows left empty close. It goes at most to just
 * below the lowest other tile (a scrolling grid has no bottom, so further down would only leave a
 * gap). From the layout before the move, so that moving back gives that layout again.
 */
export function moveOnScrollingGrid(before: readonly CellRect[], index: number, to: CellRect) : CellRect[] {
    const from = before[index]!;
    const below = tilesBelow(before, index);
    const others = before.map((rect, other) => (other === index ? { x: 0, y: 0, width: 0, height: 0 } : rect));
    const closed = shifted(others, below, upwardRoom(others, below, -from.height));
    const lowest = Math.max(1, ...closed.filter((_, other) => other !== index).map(bottomOf));
    closed[index] = { ...to, y: Math.min(to.y, lowest) };

    return removeEmptyRows(makeRoom(closed, index));
}

/**
 * A tile resized on a scrolling grid: the tiles below it move down as much as it grew, or back up
 * as much as it shrank (as far as the tiles beside them allow), and anything it then overlaps is
 * pushed down. From the layout before the resize.
 */
export function resizeOnScrollingGrid(before: readonly CellRect[], index: number, to: CellRect) : CellRect[] {
    const from = before[index]!;
    const below = tilesBelow(before, index);
    const grown = bottomOf(to) - bottomOf(from);
    const resized = [...before];
    resized[index] = to;
    const rows = grown >= 0 ? grown : upwardRoom(resized, below, grown);

    return removeEmptyRows(makeRoom(shifted(resized, below, rows), index));
}

/**
 * The lowest row a tile's bottom can be resized to on a scrolling grid: the `emptyRows` the screen
 * shows below its lowest tile, the resized tile included, so that the lowest tile can grow too
 * ([issue #39](https://github.com/activa/Casa/issues/39)). The screen grows with the tile, so
 * without a limit a corner held at the bottom edge would scroll it without end. From the layout
 * before the resize.
 */
export function lowestResizedRow(before: readonly CellRect[], emptyRows: number) : number {
    return Math.max(1, ...before.map(bottomOf)) + emptyRows - 1;
}

/**
 * Rows that no tile covers, above the lowest tile, taken out: the tiles below move up into them
 * (the owner, 2026-10-16: otherwise every tile below would have to be moved up by hand). Gaps
 * beside tiles stay; only whole empty rows go.
 */
export function removeEmptyRows(rects: readonly CellRect[]) : CellRect[] {
    const lowest = Math.max(0, ...rects.map(bottomOf));
    const covered = new Set<number>();
    for (const rect of rects) {
        for (let row = rect.y; row < bottomOf(rect); row++) {
            covered.add(row);
        }
    }

    /** By row: how many empty rows are above it. */
    const emptyAbove: number[] = [0, 0];
    for (let row = 1; row < lowest; row++) {
        emptyAbove[row + 1] = emptyAbove[row]! + (covered.has(row) ? 0 : 1);
    }

    return rects.map((rect) => {
        const shift = emptyAbove[rect.y] ?? 0;

        return shift === 0 ? rect : { ...rect, y: rect.y - shift };
    });
}

function clamp(value: number, low: number, high: number) : number {
    return Math.min(Math.max(value, low), high);
}
